import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { isApiError } from '@dorixona/api'
import { Logo, Press } from '@dorixona/ui'
import { Icon } from '../../ui/Icon'
import { useSession } from './session'
import { passwordErrorText, passwordProblem } from './model'
import type { AdminAuthApi } from './api'

export interface PasswordScreenProps {
  api: AdminAuthApi
}

/**
 * Смена временного пароля. Пока он не сменён, сервер отвечает 401 на любой
 * другой запрос, поэтому это единственный доступный экран. Смена завершает
 * остальные сеансы сотрудника и выдаёт новые токены.
 *
 * Временный пароль сотрудник только что ввёл на входе — второй раз его не
 * спрашиваем. Поле появляется, только если пароль неизвестен (страницу
 * перезагрузили) или сервер его не принял. Новый пароль вводится дважды:
 * с опечаткой сотрудник не вошёл бы и пошёл к супер-админу за сбросом.
 */
export function PasswordScreen({ api }: PasswordScreenProps) {
  const { passwordChanged, signOut, credentials } = useSession()
  const [oldPassword, setOld] = useState(credentials?.password ?? '')
  const [askOld, setAskOld] = useState(!credentials)
  const [newPassword, setNew] = useState('')
  const [repeat, setRepeat] = useState('')
  const [visible, setVisible] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const change = useMutation({
    mutationFn: () => api.changePassword(oldPassword, newPassword),
    onSuccess: (tokens) => {
      passwordChanged(tokens)
    },
    onError: (error) => {
      // Временный пароль не подошёл (его успели сбросить ещё раз) — даём
      // ввести его руками, иначе сотруднику нечего исправить.
      if (isApiError(error) && error.code === 'invalid_credentials') {
        setOld('')
        setAskOld(true)
      }
    },
  })

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    const found = passwordProblem(oldPassword, newPassword, repeat)
    setProblem(found)
    if (!found) change.mutate()
  }

  const error = problem ?? (change.error ? passwordErrorText(change.error) : null)
  const type = visible ? 'text' : 'password'

  return (
    <div className="login">
      <div className="lg-wrap">
        <div className="lg-brand">
          <Logo className="lgo" onDark />
          <div>
            <h2>Смените временный пароль</h2>
            <p>
              Временный пароль знает тот, кто его выдал. Пока он не сменён, разделы админки закрыты.
              После смены остальные сеансы будут завершены.
            </p>
          </div>
        </div>

        <form className="lg-form" onSubmit={onSubmit} noValidate>
          <div>
            <h1>Новый пароль</h1>
            <p className="sub">Не короче 8 символов и не такой же, как временный.</p>
          </div>
          {error ? (
            <div className="ui-alert" data-tone="danger" role="alert">
              <Icon name="i-alert" />
              <div className="txt">{error}</div>
            </div>
          ) : null}
          {/* Логин для менеджера паролей: так он обновит нужную запись,
              а не заведёт новую без имени. */}
          {credentials ? (
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={credentials.login}
              readOnly
              hidden
            />
          ) : null}
          {askOld ? (
            <div className="ui-field">
              <label className="ui-label" htmlFor="pw-old">
                Временный пароль
              </label>
              <input
                id="pw-old"
                className="ui-input"
                type="password"
                autoComplete="current-password"
                placeholder="Пароль от супер-админа"
                value={oldPassword}
                onChange={(e) => {
                  setOld(e.target.value)
                }}
              />
            </div>
          ) : null}
          <div className="ui-field">
            <label className="ui-label" htmlFor="pw-new">
              Новый пароль
            </label>
            <div className="lg-pw">
              <input
                id="pw-new"
                className="ui-input"
                type={type}
                autoComplete="new-password"
                placeholder="Не короче 8 символов"
                value={newPassword}
                onChange={(e) => {
                  setNew(e.target.value)
                }}
              />
              <button
                type="button"
                className="ui-btn"
                data-variant="ghost"
                aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
                onClick={() => {
                  setVisible((v) => !v)
                }}
              >
                {visible ? 'Скрыть' : 'Показать'}
              </button>
            </div>
          </div>
          <div className="ui-field">
            <label className="ui-label" htmlFor="pw-repeat">
              Повторите новый пароль
            </label>
            <input
              id="pw-repeat"
              className="ui-input"
              type={type}
              autoComplete="new-password"
              placeholder="Ещё раз, без ошибок"
              value={repeat}
              onChange={(e) => {
                setRepeat(e.target.value)
              }}
            />
          </div>
          <Press
            as="button"
            type="submit"
            className="ui-btn"
            squish="card"
            disabled={change.isPending}
            aria-busy={change.isPending}
          >
            {change.isPending ? 'Сохраняем…' : 'Сохранить и войти'}
          </Press>
          <button type="button" className="ui-btn" data-variant="secondary" onClick={signOut}>
            Выйти
          </button>
        </form>
      </div>
    </div>
  )
}
